/**
 * Product Catalog Service
 * Loads and parses the UTSCH product catalog CSV
 *
 * CSV columns:
 *   product_type | product_variant_name | product_variant_id | utsch_sku |
 *   supplier_id | cost_item_1..4 | license_plate_number_length | shipping_cost
 */

export type ProductType = 'LICENSE_PLATE' | 'EMISSION_STICKER' | 'OTHER'

export interface CatalogProduct {
  productType: ProductType
  variantName: string
  variantId: number
  utschSku: string
  supplierId: number
  costs: [number, number, number, number] // prices for qty 1, 2, 3, 4
  maxPlateLength: number | null
  shippingCost: number
}

let cachedCatalog: CatalogProduct[] | null = null
let loadPromise: Promise<CatalogProduct[]> | null = null

export async function loadProductCatalog(): Promise<CatalogProduct[]> {
  if (cachedCatalog) return cachedCatalog
  if (loadPromise) return loadPromise

  loadPromise = (async () => {
    const response = await fetch('/api/Produktkatalog.csv')
    const text = await response.text()
    const lines = text.trim().split('\n')
    const data: CatalogProduct[] = []

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim()
      if (!line) continue
      const parts = line.split(';').map(p => p.replace(/^"|"$/g, ''))
      if (parts.length < 11) continue

      const maxLenStr = parts[9]
      data.push({
        productType: parts[0] as ProductType,
        variantName: parts[1],
        variantId: parseInt(parts[2], 10),
        utschSku: parts[3],
        supplierId: parseInt(parts[4], 10),
        costs: [
          parseFloat(parts[5]) || 0,
          parseFloat(parts[6]) || 0,
          parseFloat(parts[7]) || 0,
          parseFloat(parts[8]) || 0,
        ],
        maxPlateLength: maxLenStr ? parseInt(maxLenStr, 10) : null,
        shippingCost: parseFloat(parts[10]) || 0,
      })
    }

    cachedCatalog = data
    return data
  })()

  return loadPromise
}

/**
 * Extract plate dimensions from variant name (e.g. "520x110mm")
 */
function extractDimensions(name: string): { width: number; height: number } | null {
  const match = name.match(/(\d{3})x(\d{3})mm/)
  if (!match) return null
  return { width: parseInt(match[1], 10), height: parseInt(match[2], 10) }
}

/**
 * Detect plate type from variant name
 */
export interface DetectedPlateInfo {
  width: number
  height: number
  isTwoLine: boolean
  isElectric: boolean
  isHistoric: boolean
  isSeason: boolean
  isGreen: boolean
  isCarbon: boolean
  isSchriftVerkleinert: boolean
  maxChars: number
}

export function parsePlateProduct(p: CatalogProduct): DetectedPlateInfo | null {
  if (p.productType !== 'LICENSE_PLATE') return null
  const dims = extractDimensions(p.variantName)
  if (!dims) return null
  const name = p.variantName.toLowerCase()
  return {
    ...dims,
    isTwoLine: name.includes('zweizeilig'),
    isElectric: name.startsWith('elektro'),
    isHistoric: name.startsWith('oldtimer'),
    isSeason: name.includes('saison'),
    isGreen: name.includes('grün'),
    isCarbon: name.includes('carbon'),
    isSchriftVerkleinert: name.includes('schrift verkleinert'),
    maxChars: p.maxPlateLength ?? 8,
  }
}

/**
 * Find max chars for a plate config (width, height, isSeason, isElectric, isHistoric)
 * Uses the catalog data which is authoritative.
 */
export async function findMaxChars(
  width: number,
  height: number,
  opts: { isSeason?: boolean; isElectric?: boolean; isHistoric?: boolean } = {}
): Promise<number | null> {
  const catalog = await loadProductCatalog()
  for (const p of catalog) {
    const info = parsePlateProduct(p)
    if (!info) continue
    if (info.width !== width || info.height !== height) continue
    if (info.isCarbon) continue // skip carbon variants (same rules as regular)
    if (info.isSchriftVerkleinert) continue // skip schrift verkleinert variants
    if (!!opts.isSeason !== info.isSeason) continue
    if (!!opts.isElectric !== info.isElectric) continue
    if (!!opts.isHistoric !== info.isHistoric) continue
    return info.maxChars
  }
  return null
}

/**
 * Get unique plate sizes from catalog (for a given category filter)
 */
export async function getPlateSizesFromCatalog(): Promise<Array<{ width: number; height: number; layout: 'einzeilig' | 'zweizeilig' }>> {
  const catalog = await loadProductCatalog()
  const sizes = new Map<string, { width: number; height: number; layout: 'einzeilig' | 'zweizeilig' }>()
  for (const p of catalog) {
    const info = parsePlateProduct(p)
    if (!info) continue
    if (info.isCarbon || info.isSchriftVerkleinert) continue
    const key = `${info.width}x${info.height}-${info.isTwoLine ? 'z' : 'e'}`
    if (!sizes.has(key)) {
      sizes.set(key, { width: info.width, height: info.height, layout: info.isTwoLine ? 'zweizeilig' : 'einzeilig' })
    }
  }
  return Array.from(sizes.values())
}

/**
 * Get shipping cost for a product category (uses first matching product)
 */
export async function getShippingCost(): Promise<number> {
  const catalog = await loadProductCatalog()
  const platesWithShipping = catalog.filter(p => p.productType === 'LICENSE_PLATE' && p.shippingCost > 0)
  if (platesWithShipping.length === 0) return 3.45 // sensible default
  return platesWithShipping[0].shippingCost
}
